import { acceptHMRUpdate, defineStore } from 'pinia'
import { useMaps } from '@/stores/maps'
import { toRaw } from 'vue'

export const useFindPlacesStore = defineStore('findPlaces', {
    state: () => ({
        searchInput: '',
        markers: {},
        infoWindow: null,
        loading: false
    }),
    actions: {
        async searchPlaces(location) {
            const mapsStore = useMaps()

            this.loading = true
            this.clearExistingMarkers()

            const isCoordinates = this.checkIfCoordinates(location)
            if (isCoordinates) {
                const coords = location.split(',').map(coord => parseFloat(coord.trim()))
                const latLngLocation = new mapsStore.google.maps.LatLng(coords[0], coords[1])
                
                this.createMarker(latLngLocation, 'Coordinates', 'coords', latLngLocation)
                
                toRaw(mapsStore.map).setZoom(18)
                toRaw(mapsStore.map).panTo(latLngLocation)
                this.showInfoWindow('Coordinates', this.markers['coords'], latLngLocation)
                this.loading = false
                return
            }

            const Place = mapsStore.google.maps.places.Place

            const request = {
                textQuery: location,
                fields: ['displayName', 'location', 'businessStatus'],
                includedType: '',
                useStrictTypeFiltering: false,
                isOpenNow: false,
                language: 'en-US',
                maxResultCount: 20,
                minRating: 0,
            }
            
            const { places } = await Place.searchByText(request)

            if (places.length === 1) {
                this.createMarker(places[0].location, places[0].displayName, places[0].id, places[0].location)
                
                toRaw(mapsStore.map).setZoom(18)
                toRaw(mapsStore.map).panTo(places[0].location)
                this.showInfoWindow(places[0].displayName, this.markers[places[0].id], places[0].location)
                
                this.loading = false
            } else if (places.length > 1) {
                places.forEach(place => {
                    this.createMarker(place.location, place.displayName, place.id, place.location)
                })

                const bounds = new mapsStore.google.maps.LatLngBounds()
                places.forEach(place => {
                    bounds.extend(place.location)
                })

                toRaw(mapsStore.map).fitBounds(bounds)
                this.loading = false
            } else {
                this.clearExistingMarkers()
                this.loading = false
            }
        },
        createMarker(position, title, id, location) {
            const mapsStore = useMaps()
            
            const marker = new mapsStore.google.maps.marker.AdvancedMarkerElement({
                map: toRaw(mapsStore.map),
                position: position,
                title: title,
                gmpClickable: true
            })
            
            this.markers[id] = marker
            
            marker.addEventListener('gmp-click', () => {
                toRaw(mapsStore.map).panTo(position)
                toRaw(mapsStore.map).setZoom(18)
                this.showInfoWindow(title, marker, location)
            })

            marker.addEventListener('contextmenu', (e) => {
                e.preventDefault()
                marker.setMap(null)
                delete this.markers[id]
            })
            
            return marker
        },
        clearExistingMarkers() {
            for (const id in this.markers) {
                this.markers[id].map = null
            }

            this.markers = {}
            
            if (this.infoWindow) {
                this.infoWindow.close()
                this.infoWindow = null
            }
        },
        showInfoWindow(displayName, marker, location) {
            const mapStore = useMaps()

            this.infoWindow = new mapStore.google.maps.InfoWindow({
                content: `
                    <div style="display: flex; width: 100%; justify-content: center; padding: 10px; margin-top: 14px;">
                        <span style="font-weight: 600; color: #2c3e50; width: fit-content;">
                            ${displayName}
                        </span>
                    </div>
                    <div style="padding: 10px; min-width: 200px; font-family: Arial, sans-serif;">
                        <div style="margin-bottom: 8px;">
                            <span style="margin-left: 4px; color: #2c3e50; font-family: monospace; font-size: 14px;">
                                ${location}
                            </span>
                        </div>
                    </div>
                `
            })

            this.infoWindow.open({
                map: toRaw(mapStore.map),
                anchor: marker,
            })

            if (Object.keys(this.markers).length === 1) {
                toRaw(mapStore.map).addListener('click', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close()
                        this.infoWindow = null
                    }
                })
            }
        },
        checkIfCoordinates(location) {
            const coordRegex = /^(-?([1-8]?\d(\.\d+)?|90(\.0+)?)),\s*(-?(1[0-7]\d(\.\d+)?|[1-9]?\d(\.\d+)?|180(\.0+)?))$/
            return coordRegex.test(location)
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useFindPlacesStore, import.meta.hot))
}